import React from "react";
import PropTypes from "prop-types";
import {
  Modal,
  Typography,
  Row,
  Col,
  Button,
  Card,
  Space,
  Tag,
  Divider,
} from "antd";
import {
  UserOutlined,
  CarOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  CommentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { formatCurrencyCOP } from "../../../utils/formatCurrency";

const { Title, Text } = Typography;

// Función auxiliar para obtener el estado visual del traslado con colores de MOGOTAX SAS
const getStatusConfig = (status) => {
  const statusMap = {
    Pendiente: {
      color: "#fa541c",
      bgColor: "#fff7e6",
      borderColor: "#fa541c",
      icon: <ClockCircleOutlined />,
    },
    "En Proceso": {
      color: "#0a2e5c",
      bgColor: "#e6f7ff",
      borderColor: "#0a2e5c",
      icon: <ExclamationCircleOutlined />,
    },
    Completado: {
      color: "#52c41a",
      bgColor: "#f6ffed",
      borderColor: "#52c41a",
      icon: <CheckCircleOutlined />,
    },
    Cancelado: {
      color: "#b10e1a",
      bgColor: "#fff1f0",
      borderColor: "#b10e1a",
      icon: <ExclamationCircleOutlined />,
    },
    Confirmado: {
      color: "#52c41a",
      bgColor: "#f6ffed",
      borderColor: "#52c41a",
      icon: <CheckCircleOutlined />,
    },
    Asignado: {
      color: "#1f7362",
      bgColor: "#e6f7ff",
      borderColor: "#1f7362",
      icon: <UserOutlined />,
    },
    "Pre-Asignado": {
      color: "#0c27a0",
      bgColor: "#f9f0ff",
      borderColor: "#0c27a0",
      icon: <CalendarOutlined />,
    },
    Programado: {
      color: "#6c06b5",
      bgColor: "#f9f0ff",
      borderColor: "#6c06b5",
      icon: <CalendarOutlined />,
    },
    Finalizado: {
      color: "#4d4d4d",
      bgColor: "#f5f5f5",
      borderColor: "#4d4d4d",
      icon: <CheckCircleOutlined />,
    },
    Fallido: {
      color: "#4a2c04",
      bgColor: "#fff7e6",
      borderColor: "#4a2c04",
      icon: <ExclamationCircleOutlined />,
    },
    Enterado: {
      color: "#0c5b07",
      bgColor: "#f6ffed",
      borderColor: "#0c5b07",
      icon: <CheckCircleOutlined />,
    },
    Iniciado: {
      color: "#fa7114",
      bgColor: "#fff7e6",
      borderColor: "#fa7114",
      icon: <ExclamationCircleOutlined />,
    },
    Aceptado: {
      color: "#088A08",
      bgColor: "#f6ffed",
      borderColor: "#088A08",
      icon: <CheckCircleOutlined />,
    },
    "En Camino": {
      color: "#FFBF00",
      bgColor: "#fffbe6",
      borderColor: "#FFBF00",
      icon: <ExclamationCircleOutlined />,
    },
    Esperando: {
      color: "#a44706",
      bgColor: "#fff7e6",
      borderColor: "#a44706",
      icon: <ClockCircleOutlined />,
    },
    "Inicio Exitosamente": {
      color: "#52c41a",
      bgColor: "#f6ffed",
      borderColor: "#52c41a",
      icon: <CheckCircleOutlined />,
    },
    "Finalizó Exitosamente": {
      color: "#52c41a",
      bgColor: "#f6ffed",
      borderColor: "#52c41a",
      icon: <CheckCircleOutlined />,
    },
  };
  return (
    statusMap[status] || {
      color: "#b0b500",
      bgColor: "#fafafa",
      borderColor: "#b0b500",
      icon: <InfoCircleOutlined />,
    }
  );
};

// Componente InfoCard con el estilo de MOGOTAX
const InfoCard = ({ title, icon, children }) => (
  <Card
    style={{
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      marginBottom: "16px",
      border: "1px solid #f0f0f0",
      borderLeftWidth: "4px",
      borderLeftColor: "#0a2e5c",
      backdropFilter: "blur(10px)",
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    }}
    styles={{
      header: {
        borderBottom: "1px solid #f0f0f0",
        paddingBottom: "12px",
        backgroundColor: "#fafafa",
      },
      body: {
        padding: "16px",
      }
    }}
    title={
      <Space align="center" size="small">
        <div
          style={{
            padding: "6px",
            borderRadius: "6px",
            backgroundColor: "#e6f7ff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#0a2e5c",
          }}
        >
          {icon}
        </div>
        <Title
          level={5}
          style={{ margin: 0, fontWeight: 500, color: "#0a2e5c" }}
        >
          {title}
        </Title>
      </Space>
    }
  >
    {children}
  </Card>
);

// Componente para mostrar información con estilo MOGOTAX
const InfoRow = ({ label, value, icon, type = "text" }) => (
  <div
    style={{
      marginBottom: "8px",
      display: "flex",
      alignItems: "flex-start",
      gap: "8px",
    }}
  >
    {icon && <div style={{ marginTop: "2px", color: "#0a2e5c" }}>{icon}</div>}
    <div style={{ flex: 1 }}>
      <Text
        style={{
          fontSize: "12px",
          color: "#8c8c8c",
          fontWeight: 500,
          display: "block",
          marginBottom: "2px",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </Text>
      <Text
        style={{
          fontSize: "14px",
          color: "#0a2e5c",
          fontWeight: 500,
          lineHeight: "1.4",
        }}
      >
        {type === "currency"
          ? formatCurrencyCOP(value) || "$ 0,00"
          : value || "N/A"}
      </Text>
    </div>
  </div>
);

function VerTrasladoDialog({ open, onClose, traslado }) {
  if (!traslado) return null;

  const statusConfig = getStatusConfig(traslado.status);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={[
        <Button
          key="close"
          type="primary"
          size="large"
          style={{
            backgroundColor: "#0a2e5c",
            borderColor: "#0a2e5c",
            borderRadius: "4px",
            fontWeight: 500,
            height: "38px",
            minWidth: "110px",
            fontSize: "15px",
          }}
          onClick={onClose}
        >
          Cerrar
        </Button>,
      ]}
      title={
        <div
          style={{
            background: `linear-gradient(90deg, ${statusConfig.color}, ${statusConfig.borderColor})`,
            padding: "12px 20px",
            margin: "-20px -24px 20px -24px",
            borderRadius: "8px 8px 0 0",
            color: "white",
            boxShadow: `0 2px 8px ${statusConfig.color}20`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space align="center" size="middle">
              <div
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backdropFilter: "blur(10px)",
                }}
              >
                <InfoCircleOutlined
                  style={{ fontSize: "20px", color: "white" }}
                />
              </div>
              <div>
                <Title level={4} style={{ margin: 0, color: "white" }}>
                  Traslado CS - {traslado.id}
                </Title>
                <Text
                  style={{
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "13px",
                    fontWeight: 400,
                  }}
                >
                  Información detallada del servicio
                </Text>
              </div>
            </Space>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                color: "white",
                padding: "8px 16px",
                borderRadius: "6px",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: 600,
                backdropFilter: "blur(10px)",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              {statusConfig.icon}
              {traslado.status}
            </div>
          </div>
        </div>
      }
      width={1400}
      style={{
        top: 10,
        padding: 0,
        maxHeight: "96vh",
      }}
      styles={{
        body: {
          padding: 8,
          minHeight: 0,
          maxHeight: "80vh",
          overflowY: "auto",
        }
      }}
      centered
      destroyOnHidden
    >
      <div
        style={{
          padding: 0,
          background: "linear-gradient(135deg, #f5f7fa 0%, #e6f7ff 100%)",
          borderRadius: "0 0 8px 8px",
        }}
      >
        <Row gutter={[10, 10]}>
          {/* Información del Usuario y Médica */}
          <Col xs={24} md={12} lg={6} xl={6}>
            <InfoCard
              title="Información del Paciente"
              icon={<UserOutlined style={{ fontSize: "15px" }} />}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={2}>
                <InfoRow
                  label="Nombre completo"
                  value={traslado.nombre_paciente}
                  icon={<UserOutlined style={{ fontSize: "11px" }} />}
                />
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Tipo de usuario"
                  value={traslado.tipo_usuario}
                />
                <InfoRow
                  label="Tipo de documento"
                  value={traslado.tipo_documento_paciente}
                />
                <InfoRow
                  label="Número de documento"
                  value={traslado.numero_documento_paciente}
                  icon={<FileTextOutlined style={{ fontSize: "11px" }} />}
                />
                <InfoRow
                  label="Zona"
                  value={traslado.zona_paciente}
                />
              </Space>
            </InfoCard>

            <InfoCard
              title="Información Médica"
              icon={<FileTextOutlined style={{ fontSize: "15px" }} />}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={2}>
                <InfoRow
                  label="EPS"
                  value={traslado.empresa}
                  icon={<FileTextOutlined style={{ fontSize: "11px" }} />}
                />
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Preautorización"
                  value={traslado.preautorizacion}
                />
                <InfoRow 
                  label="Autorización" 
                  value={
                    typeof traslado.autorizacion === 'object' && traslado.autorizacion 
                      ? traslado.autorizacion.numero_autorizacion || traslado.autorizacion.numero_pre_autorizacion || 'N/A'
                      : traslado.autorizacion || 'N/A'
                  } 
                />
                <InfoRow label="Procedimiento" value={traslado.proposito} />
                <InfoRow
                  label="Diagnóstico"
                  value={traslado.diagnostico_nombre}
                />
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Copago Total"
                  value={traslado.copago}
                  type="currency"
                  icon={<DollarOutlined style={{ fontSize: "11px" }} />}
                />
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Copago Servicio"
                  value={traslado.copago_diario}
                  type="currency"
                  icon={<DollarOutlined style={{ fontSize: "11px" }} />}
                />
              </Space>
            </InfoCard>
          </Col>

          {/* Vehículo, Conductor y Estado */}
          <Col xs={24} md={12} lg={6} xl={6}>
            <InfoCard
              title="Vehículo y Conductor"
              icon={<CarOutlined style={{ fontSize: "15px" }} />}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={2}>
                <InfoRow
                  label="Vehículo asignado"
                  value={traslado.nombre_vehiculo || "Sin Asignar"}
                  icon={<CarOutlined style={{ fontSize: "11px" }} />}
                />
                <InfoRow
                  label="Placa del vehículo"
                  value={traslado.placa || "Sin Asignar"}
                />
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Conductor asignado"
                  value={traslado.nombre_conductor || "Sin Asignar"}
                  icon={<UserOutlined style={{ fontSize: "11px" }} />}
                />
                {traslado.nombre_conductor && (
                  <div style={{ marginTop: "4px" }}>
                    <Tag
                      color="#0a2e5c"
                      style={{
                        backgroundColor: "#0a2e5c",
                        borderColor: "#0a2e5c",
                        color: "#fff",
                        borderRadius: "4px",
                        fontSize: "11px",
                        padding: "2px 8px",
                      }}
                    >
                      Conductor Asignado
                    </Tag>
                  </div>
                )}
              </Space>
            </InfoCard>

            <InfoCard
              title="Estado del Traslado"
              icon={<CheckCircleOutlined style={{ fontSize: "15px" }} />}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={2}>
                <div style={{ marginBottom: "6px" }}>
                  <Text
                    style={{
                      fontSize: "11px",
                      color: "#8c8c8c",
                      fontWeight: 500,
                      display: "block",
                      marginBottom: "3px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Estado actual
                  </Text>
                  <div
                    style={{
                      backgroundColor: statusConfig.bgColor,
                      color: statusConfig.color,
                      padding: "4px 10px",
                      borderRadius: "4px",
                      border: `1px solid ${statusConfig.borderColor}`,
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                      fontSize: "13px",
                      fontWeight: 500,
                      width: "fit-content",
                    }}
                  >
                    {statusConfig.icon}
                    {traslado.status}
                  </div>
                </div>
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Seguimiento"
                  value={traslado.seguimiento}
                  icon={<ClockCircleOutlined style={{ fontSize: "11px" }} />}
                />
                <InfoRow
                  label="Confirmación"
                  value={traslado.confirmacion}
                  icon={<CheckCircleOutlined style={{ fontSize: "11px" }} />}
                />
              </Space>
            </InfoCard>
          </Col>

          {/* Origen, Destino y Observaciones */}
          <Col xs={24} md={12} lg={6} xl={6}>
            <InfoCard
              title="Punto de Origen"
              icon={<EnvironmentOutlined style={{ fontSize: "15px" }} />}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={2}>
                <InfoRow
                  label="Lugar"
                  value={traslado.origen_lugar || "Domicilio"}
                  icon={<EnvironmentOutlined style={{ fontSize: "11px" }} />}
                />
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Departamento"
                  value={traslado.origen_departamento}
                />
                <InfoRow label="Ciudad" value={traslado.origen_ciudad} />
                <InfoRow
                  label="Dirección completa"
                  value={traslado.origen_direccion}
                />
                {traslado.origen_vereda_corregimiento && (
                  <InfoRow
                    label="Vereda/Corregimiento"
                    value={traslado.origen_vereda_corregimiento}
                  />
                )}
              </Space>
            </InfoCard>

            <InfoCard
              title="Punto de Destino"
              icon={<EnvironmentOutlined style={{ fontSize: "15px" }} />}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={2}>
                <InfoRow
                  label="Lugar"
                  value={traslado.destino_lugar || "Domicilio"}
                  icon={<EnvironmentOutlined style={{ fontSize: "11px" }} />}
                />
                <Divider style={{ margin: "4px 0", borderColor: "#f0f0f0" }} />
                <InfoRow
                  label="Departamento"
                  value={traslado.destino_departamento}
                />
                <InfoRow label="Ciudad" value={traslado.destino_ciudad} />
                <InfoRow
                  label="Dirección completa"
                  value={traslado.destino_direccion}
                />
                {traslado.destino_vereda_corregimiento && (
                  <InfoRow
                    label="Vereda/Corregimiento"
                    value={traslado.destino_vereda_corregimiento}
                  />
                )}
              </Space>
            </InfoCard>
          </Col>

          <Col xs={24} md={12} lg={6} xl={6}>
            <InfoCard
              title="Observaciones Internas"
              icon={<CommentOutlined style={{ fontSize: "15px" }} />}
            >
              <div
                style={{
                  padding: "8px",
                  backgroundColor: "#fafafa",
                  borderRadius: "4px",
                  border: "1px solid #f0f0f0",
                  minHeight: "40px",
                  fontSize: "13px",
                }}
              >
                <Text
                  style={{
                    fontSize: "13px",
                    color: "#0a2e5c",
                    lineHeight: "1.4",
                    fontStyle: traslado.observaciones_traslado
                      ? "normal"
                      : "italic",
                    fontWeight: traslado.observaciones_traslado ? 400 : 300,
                  }}
                >
                  {traslado.observaciones_traslado ||
                    "Sin observaciones internas para este traslado"}
                </Text>
              </div>
            </InfoCard>

            <InfoCard
              title="Observaciones de Conductor"
              icon={<CommentOutlined style={{ fontSize: "15px" }} />}
            >
              <div
                style={{
                  padding: "8px",
                  backgroundColor: "#fafafa",
                  borderRadius: "4px",
                  border: "1px solid #f0f0f0",
                  minHeight: "40px",
                  fontSize: "13px",
                }}
              >
                <Text
                  style={{
                    fontSize: "13px",
                    color: "#0a2e5c",
                    lineHeight: "1.4",
                    fontStyle: traslado.indicaciones_conductor
                      ? "normal"
                      : "italic",
                    fontWeight: traslado.indicaciones_conductor ? 400 : 300,
                  }}
                >
                  {traslado.indicaciones_conductor ||
                    "Sin observaciones del conductor para este traslado"}
                </Text>
              </div>
            </InfoCard>

            <InfoCard
              title="Motivo de Cierre"
              icon={<ExclamationCircleOutlined style={{ fontSize: "15px" }} />}
            >
              <div
                style={{
                  padding: "8px",
                  backgroundColor: "#fafafa",
                  borderRadius: "4px",
                  border: "1px solid #f0f0f0",
                  minHeight: "40px",
                  fontSize: "13px",
                }}
              >
                <Text
                  style={{
                    fontSize: "13px",
                    color: "#0a2e5c",
                    lineHeight: "1.4",
                    fontStyle: traslado.motivo_cancelacion
                      ? "normal"
                      : "italic",
                    fontWeight: traslado.motivo_cancelacion ? 400 : 300,
                  }}
                >
                  {traslado.motivo_cancelacion ||
                    "Sin motivo de cancelación para este traslado"}
                </Text>
              </div>
            </InfoCard>
          </Col>
        </Row>
      </div>
    </Modal>
  );
}

// **Validación de las props con PropTypes**
InfoCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.element,
  children: PropTypes.node,
  borderColor: PropTypes.string,
};

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.element,
  type: PropTypes.oneOf(["text", "currency"]),
};

// Validación de las props con prop-types
VerTrasladoDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  traslado: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    nombre_paciente: PropTypes.string,
    tipo_usuario: PropTypes.string,
    tipo_documento_paciente: PropTypes.string,
    numero_documento_paciente: PropTypes.string,
    empresa: PropTypes.string,
    preautorizacion: PropTypes.string,
    autorizacion: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        numero_autorizacion: PropTypes.string,
        numero_pre_autorizacion: PropTypes.string,
        codigo_mipres: PropTypes.string,
      })
    ]),
    proposito: PropTypes.string,
    diagnostico_nombre: PropTypes.string,
    copago: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nombre_vehiculo: PropTypes.string,
    placa: PropTypes.string,
    nombre_conductor: PropTypes.string,
    status: PropTypes.string,
    seguimiento: PropTypes.string,
    confirmacion: PropTypes.string,
    origen_lugar: PropTypes.string,
    origen_departamento: PropTypes.string,
    origen_ciudad: PropTypes.string,
    origen_direccion: PropTypes.string,
    destino_lugar: PropTypes.string,
    destino_departamento: PropTypes.string,
    destino_ciudad: PropTypes.string,
    destino_direccion: PropTypes.string,
    observaciones_traslado: PropTypes.string,
    indicaciones_conductor: PropTypes.string,
    motivo_cancelacion: PropTypes.string,
  }),
};

export default VerTrasladoDialog;
